import type { BundleFileResponse, BundleGroupCreate, BundleGroupResponse, EmailAttachmentInfoResponse } from "@/hooks/useBundle";
import { cleanFileName, getFileExtension, tempId } from "@/utils/file-utils";

export interface AffectedBundle {
  bundle_id: string;
  bundle_title: string;
  affected_file_count: number;
}

export type FontOption = {
  value: string;
  label: string;
  fontFamily: string;
  files: {
    regular: string;
    bold: string;
    italic: string;
    bold_italic: string;
  };
};

export type TextAlignment = 'left' | 'center' | 'right';
export type VolumeNaming = 'numeric' | 'alpha';

export enum GroupType {
  SECTION = 'section',
  VOLUME = 'volume',
  ROOT = 'root',
  EMAIL = 'email',
}

export type FontStyle = {
  font_family: string;
  font_size: number;
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  alignment: TextAlignment;
};

export type FontStyleWithBackground = FontStyle & {
  background: string;
};

export type IndexTitle = FontStyle & {
  enabled: boolean;
  text: string;
};

export type IndexColumnKey = 'tab' | 'document' | 'date' | 'page_ref';

export type IndexColumn = {
  key: IndexColumnKey;
  visible: boolean;
};

export type PageNumber = FontStyle & {
  enabled: boolean;
  placement: 'top' | 'bottom';
  skip_title_page: boolean;
  skip_volume_divider: boolean;
  skip_main_index: boolean;
  skip_volume_index: boolean;
  roman_index: boolean;
  index_per_volume: boolean;
  document_per_volume: boolean;
};

export type BatesStamp = FontStyle & {
  enabled: boolean;
  placement: 'top' | 'bottom';
  prefix: string;
  suffix: string;
  start_number: number;
  num_digits: number;
  skip_title_page: boolean;
  skip_volume_divider: boolean;
  skip_main_index: boolean;
  skip_volume_index: boolean;
  roman_index: boolean;
  index_per_volume: boolean;
  document_per_volume: boolean;
  volume_prefix: boolean;
};

export type TitlePageConfig = {
  enabled: boolean;
  format: 'pdf' | 'docx';
  edited: boolean;
};

export type SpreadsheetOrientation = 'auto' | 'portrait' | 'landscape';

/** How text wider than its column is handled.
 *  - 'clip': honour saved widths, truncate like Excel print.
 *  - 'autofit': widen columns to content so nothing is cut off.
 *  - 'wrap': wrap long cells and grow rows. */
export type SpreadsheetOverflow = 'clip' | 'autofit' | 'wrap';

/** Whether cell gridlines are printed. */
export type SpreadsheetGridlines = 'on' | 'off';

/** Whether row numbers and column letters are printed. */
export type SpreadsheetHeadings = 'on' | 'off';

/** Sheet-name stamp on multi-sheet workbook pages. Same knobs as page numbers. */
export type SpreadsheetSheetName = FontStyle & {
  enabled: boolean;
  placement: 'top' | 'bottom';
};

export type SpreadsheetSettings = {
  orientation: SpreadsheetOrientation;
  overflow: SpreadsheetOverflow;
  gridlines: SpreadsheetGridlines;
  headings: SpreadsheetHeadings;
  sheet_name: SpreadsheetSheetName;
};

export type BundleMetadata = {
  volume_naming: VolumeNaming;
  volume_divider_page: boolean;
  per_volume_index: boolean;
  volume_heading: FontStyle;
  volume_divider_title: FontStyle;
  section_heading: FontStyleWithBackground;
  document_row: FontStyle;
  index_title: IndexTitle;
  column_headers: FontStyleWithBackground;
  index_columns: IndexColumn[];
  date_format: string;
  index_on_title_page: boolean;
  remove_blank_pages: boolean;
  spreadsheet: SpreadsheetSettings;
  show_email_attachments_in_index: boolean;
  page_numbers: PageNumber;
  bates_stamp: BatesStamp;
  title_page: TitlePageConfig;
};

export const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: '31/12/2025' },
  { value: 'MM/DD/YYYY', label: '12/31/2025' },
  { value: 'YYYY-MM-DD', label: '2025-12-31' },
  { value: 'DD MMM YYYY', label: '31 Dec 2025' },
  { value: 'DD MMMM YYYY', label: '31 December 2025' },
  { value: 'MMM DD, YYYY', label: 'Dec 31, 2025' },
  { value: 'MMMM DD, YYYY', label: 'December 31, 2025' },
];

export const INDEX_COLUMN_LABELS: Record<IndexColumnKey, string> = {
  tab: 'Tab',
  document: 'Document Description',
  date: 'Date',
  page_ref: 'Page Ref',
};

export type GroupNode = {
  groupId: string;
  title: string;
  parentGroupId: string | null;
  groupType: GroupType;
  isNew?: boolean;
  /** Ordered IDs of this group's children (mixed group IDs and file IDs). */
  children: string[];
};

export type FileNode = {
  fileId: string;
  fileReferenceGuid: string;
  fileName: string;
  fileExtension: string;
  customName: string | null;
  pageRange: string | null;
  /** null = file lives at the bundle root (serialized into a synthetic ROOT proxy on save). */
  parentGroupId: string | null;
  /** For EMAIL attachments: false = shown with the email (unchecked) but NOT exported to the bundle.
   *  Undefined / true = included. Filtered out of the save payload; regenerated from email metadata on load. */
  includedInBundle?: boolean;
};

export type BundleConfig = {
  /** Ordered IDs at the top level (mixed group IDs and file IDs, same as GroupNode.children). */
  rootChildIds: string[];
  groups: Record<string, GroupNode>;
  files: Record<string, FileNode>;
};

/** For each fileReferenceGuid that appears more than once, return all occurrences with group context */
export type DuplicateEntry = { fileId: string; groupTitle: string; fileName: string };
export type DuplicateMap = Record<string, DuplicateEntry[]>;

export function countResponseFiles(groups: BundleGroupResponse[]): number {
  return groups.reduce((sum, g) => sum + g.files.length + countResponseFiles(g.subgroups), 0);
}

export function buildDuplicateMap(state: BundleConfig): DuplicateMap {
  const byGuid: Record<string, DuplicateEntry[]> = {};
  for (const file of Object.values(state.files)) {
    if (file.includedInBundle === false) continue;
    const group = file.parentGroupId ? state.groups[file.parentGroupId] : null;
    const groupTitle = file.parentGroupId === null ? 'Root' : (group?.title ?? 'Unknown');
    const entry: DuplicateEntry = { fileId: file.fileId, groupTitle, fileName: file.customName || file.fileName };
    (byGuid[file.fileReferenceGuid] ??= []).push(entry);
  }
  const result: DuplicateMap = {};
  for (const [guid, entries] of Object.entries(byGuid)) {
    if (entries.length > 1) result[guid] = entries;
  }
  return result;
}

export function getSubtreeDepth(state: BundleConfig, groupId: string): number {
  const children = state.groups[groupId]?.children || [];
  let max = 0;
  for (const childId of children) {
    if (state.groups[childId]) {
      max = Math.max(max, 1 + getSubtreeDepth(state, childId));
    }
  }
  return max;
}

/** Counts used by a SECTION row's header / empty-state checks.
 *  - `docCount` = direct included files + emails (email file + included attachments)
 *  - `subCount` = SECTION subgroups
 *  - `isEmpty`  = this group has no children at all
 *
 *  Shared by `EditSection` and `ViewSection` so their "N docs · M sections" header reads the
 *  same. Unincluded email attachments are excluded from the doc count — they're checkbox-off
 *  UI rows, not actual bundle documents. */
export function sectionChildCounts(state: BundleConfig, groupId: string): { docCount: number; subCount: number; isEmpty: boolean } {
  const childIds = state.groups[groupId]?.children ?? [];
  let docCount = 0;
  let subCount = 0;
  for (const id of childIds) {
    const file = state.files[id];
    if (file) {
      if (file.includedInBundle !== false) docCount++;
      continue;
    }
    const sub = state.groups[id];
    if (!sub) continue;
    if (sub.groupType === GroupType.EMAIL) {
      for (const cid of sub.children ?? []) {
        const f = state.files[cid];
        if (f && f.includedInBundle !== false) docCount++;
      }
    } else if (sub.groupType === GroupType.SECTION) {
      subCount++;
    }
  }
  return { docCount, subCount, isEmpty: childIds.length === 0 };
}

export function countGroupsAndFiles(state: BundleConfig): { volumeCount: number; sectionCount: number; fileCount: number } {
  let volumeCount = 0;
  let sectionCount = 0;
  let fileCount = 0;
  for (const g of Object.values(state.groups)) {
    if (g.groupType === GroupType.VOLUME) volumeCount++;
    else if (g.groupType === GroupType.SECTION) sectionCount++;
  }
  for (const f of Object.values(state.files)) {
    if (f.includedInBundle === false) continue;
    fileCount++;
  }
  return { volumeCount, sectionCount, fileCount };
}

/** In an EMAIL group, the email file itself is always the first child. */
function getEmailFileId(state: BundleConfig, groupId: string): string | undefined {
  return state.groups[groupId]?.children?.[0];
}

/** In an EMAIL group, returns the fileReferenceGuid of the email file (its first child). */
export function getEmailFileGuid(state: BundleConfig, groupId: string): string | undefined {
  const fileId = getEmailFileId(state, groupId);
  return fileId ? state.files[fileId]?.fileReferenceGuid : undefined;
}

/** Convert API response tree → flat normalized state.
 *  `emailAttachmentsByGroupId` comes from `BundleResponse.email_attachments` — a top-level
 *  read-only map the backend recomputes on every fetch. Keyed by `bundle_group_id`, only
 *  populated for EMAIL groups. */
export function fromGroups(
  rootGroups: BundleGroupResponse[],
  emailAttachmentsByGroupId: Record<string, EmailAttachmentInfoResponse[]> = {},
): BundleConfig {
  const state: BundleConfig = { rootChildIds: [], groups: {}, files: {} };

  function walk(groups: BundleGroupResponse[], parentGroupId: string | null) {
    const sorted = [...groups].sort((a, b) => a.order_index - b.order_index);
    for (const group of sorted) {
      const id = group.bundle_group_id;

      // Backend ROOT proxies only exist to give loose root-level files a home in the stored
      // tree — lift them into rootChildIds so the UI doesn't juggle phantom groups.
      // Synthesized back in toGroups on save.
      if (parentGroupId === null && group.group_type === GroupType.ROOT) {
        liftRootProxyFiles(state, group);
        continue;
      }

      if (parentGroupId === null) state.rootChildIds.push(id);
      state.groups[id] = {
        groupId: id,
        title: group.title,
        parentGroupId,
        groupType: group.group_type,
        children: [],
      };

      // Build the child-id list by interleaving files and subgroups by order_index.
      const children: string[] = [];
      for (const entry of mergeFilesAndSubgroups(group.files, group.subgroups)) {
        if (entry.kind === 'file') {
          children.push(entry.file.bundle_file_id);
          state.files[entry.file.bundle_file_id] = fileNodeFrom(entry.file, id);
        } else {
          children.push(entry.group.bundle_group_id);
        }
      }

      // EMAIL groups get a special child-list pass so every attachment (included + not) is a
      // FileNode in the group's children — one source of truth for ordering, drag & drop, rendering.
      state.groups[id].children = group.group_type === GroupType.EMAIL
        ? reconcileEmailChildren(state, id, children, emailAttachmentsByGroupId[id] ?? [])
        : children;

      walk(group.subgroups, id);
    }
  }

  walk(rootGroups, null);
  return state;
}

// ── fromGroups helpers ──

type OrderedChild =
  | { kind: 'file'; file: BundleFileResponse }
  | { kind: 'group'; group: BundleGroupResponse };

/** Interleave a group's files and subgroups by order_index. On ties, files come first —
 *  legacy-compat behaviour from when files and subgroups were separate arrays at the API layer. */
function mergeFilesAndSubgroups(files: BundleFileResponse[], subgroups: BundleGroupResponse[]): OrderedChild[] {
  const items: (OrderedChild & { order_index: number })[] = [
    ...files.map(file => ({ kind: 'file' as const, file, order_index: file.order_index })),
    ...subgroups.map(group => ({ kind: 'group' as const, group, order_index: group.order_index })),
  ];
  items.sort((a, b) => a.order_index - b.order_index || (a.kind === 'file' ? -1 : 1));
  return items;
}

/** Build a FileNode from the API's BundleFileResponse. Used both in the normal tree walk
 *  and when lifting a ROOT-proxy group's files into rootChildIds (parentGroupId=null). */
function fileNodeFrom(f: BundleFileResponse, parentGroupId: string | null): FileNode {
  return {
    fileId: f.bundle_file_id,
    fileReferenceGuid: f.file_reference_guid,
    customName: f.custom_name,
    pageRange: f.page_range,
    fileName: cleanFileName(f.file_name),
    fileExtension: getFileExtension(f.source_file_name || f.file_name),
    parentGroupId,
  };
}

/** Drop a ROOT-proxy group from UI state, lifting its files into rootChildIds. */
function liftRootProxyFiles(state: BundleConfig, group: BundleGroupResponse) {
  const sortedFiles = [...group.files].sort((a, b) => a.order_index - b.order_index);
  for (const f of sortedFiles) {
    state.rootChildIds.push(f.bundle_file_id);
    state.files[f.bundle_file_id] = fileNodeFrom(f, null);
  }
}

/** Merge saved-tree attachments with the full attachment metadata for an EMAIL group so every
 *  attachment (included + unincluded) becomes a FileNode in the returned children list.
 *  Saved (included) attachments come first in their saved order — this preserves any manual
 *  reordering the user did. Unincluded attachments (those in metadata but not in the saved
 *  tree) are materialised as ghost FileNodes with `includedInBundle: false` and appended. */
function reconcileEmailChildren(
  state: BundleConfig,
  groupId: string,
  savedChildren: string[],
  rawAttachments: EmailAttachmentInfoResponse[],
): string[] {
  const emailFileId = savedChildren[0];
  const savedAttachments = savedChildren.slice(1).filter(cid => !!state.files[cid]);
  const savedGuids = new Set(savedAttachments.map(fid => state.files[fid]!.fileReferenceGuid));

  const unified: string[] = emailFileId ? [emailFileId] : [];
  // Saved / included attachments keep their persisted order — this is the source of truth
  // for user-driven reordering. Iterating rawAttachments instead would clobber any manual
  // swap with whatever order `has_attachment` rows happen to be in.
  for (const fid of savedAttachments) unified.push(fid);
  // Append ghosts for metadata attachments the bundle doesn't have (i.e., user unchecked them).
  for (const meta of rawAttachments) {
    if (savedGuids.has(meta.file_reference_guid)) continue;
    const ghostId = tempId();
    state.files[ghostId] = {
      fileId: ghostId,
      fileReferenceGuid: meta.file_reference_guid,
      customName: null,
      pageRange: null,
      fileName: cleanFileName(meta.file_name),
      fileExtension: getFileExtension(meta.source_file_name || meta.file_name),
      parentGroupId: groupId,
      includedInBundle: false,
    };
    unified.push(ghostId);
  }
  return unified;
}

/** Strip temp- prefixed IDs so the backend generates proper UUIDs. */
function apiId(id: string): string | null {
  return id.startsWith('temp-') ? null : id;
}

/** Convert flat normalized state → nested BundleGroupCreate[] for the API.
 *  Contiguous runs of root-level files are wrapped in a synthetic ROOT proxy group so
 *  the backend (which requires every file to live under a group) receives a valid tree.
 */
export function toGroups(state: BundleConfig): BundleGroupCreate[] {
  function buildGroup(groupId: string, orderIndex: number): BundleGroupCreate {
    const group = state.groups[groupId];
    const children = group?.children || [];
    const files: BundleGroupCreate['files'] = [];
    const subgroups: BundleGroupCreate[] = [];

    let orderCounter = 0;
    for (const childId of children) {
      const file = state.files[childId];
      if (file) {
        // Unincluded email attachments are UI-only — not persisted. Backend regenerates them
        // from the email's metadata on next load.
        if (file.includedInBundle === false) continue;
        files.push(toFileCreate(file, orderCounter++));
      } else {
        subgroups.push(buildGroup(childId, orderCounter++));
      }
    }

    return {
      bundle_group_id: apiId(group.groupId),
      title: group.title,
      order_index: orderIndex,
      group_type: group.groupType,
      files,
      subgroups,
    };
  }

  const result: BundleGroupCreate[] = [];
  let pending: FileNode[] = [];
  const flush = () => {
    if (pending.length === 0) return;
    result.push({
      bundle_group_id: null,
      title: '__root__',
      order_index: result.length,
      group_type: GroupType.ROOT,
      files: pending.map((f, i) => toFileCreate(f, i)),
      subgroups: [],
    });
    pending = [];
  };

  for (const id of state.rootChildIds) {
    const file = state.files[id];
    if (file) {
      pending.push(file);
    } else {
      flush();
      result.push(buildGroup(id, result.length));
    }
  }
  flush();

  // Re-assign order_index sequentially after the flush/group interleave
  return result.map((g, i) => ({ ...g, order_index: i }));
}

function toFileCreate(file: FileNode, orderIndex: number): BundleGroupCreate['files'][number] {
  return {
    bundle_file_id: apiId(file.fileId),
    file_reference_guid: file.fileReferenceGuid,
    order_index: orderIndex,
    custom_name: file.customName,
    page_range: file.pageRange,
  };
}

/** Convert a 1-based index to an alpha label: 1→A, 2→B, ..., 26→Z, 27→AA, 28→AB */
export function indexToAlpha(n: number): string {
  let result = '';
  while (n > 0) {
    n--;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

/** Generate the next unused volume name based on naming mode */
export function nextVolumeName(naming: VolumeNaming, existingNames: string[]): string {
  const taken = new Set(existingNames.map(n => n.toLowerCase()));
  for (let i = 1; ; i++) {
    const candidate = naming === 'numeric' ? `Volume ${i}` : `Volume ${indexToAlpha(i)}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** Check if a volume name matches the auto-naming pattern */
export function isAutoVolumeName(name: string): boolean {
  return /^Volume\s+(\d+|[A-Z]+)$/i.test(name.trim());
}

/** Bates/page-stamp fragment from a volume title: drop a leading "Volume" word. */
export function volumeStampLabel(title: string): string {
  return title.trim().replace(/^volume(?:\s+|$)/i, '').trim();
}
